import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import customerApi from "@/lib/customerApi";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import FareSummary from "@/components/FareSummary";
import PassengerPicker from "@/components/PassengerPicker";
import CustomerInfoForm from "@/components/CustomerInfoForm";
import TripCloseoutDialog from "@/components/TripCloseoutDialog";
import TripNavButtons from "@/components/TripNavButtons";
import TripMap from "@/components/TripMap";
import { Calculator, Plus, X, MapPin, Loader2 } from "lucide-react";

/**
 * v2.6.9 batch BE-2 · Rider · TAXIquote (formerly Custom Trip)
 *
 * Fully automated quote builder for drivers:
 *   - Driver enters pickup + dropoff (+ optional stops + passenger count)
 *   - Server pulls all rates from admin pricing config, computes the quote
 *     via the same engine the rider /customer/quote uses
 *   - Auto airport-fee, auto toll-fee, auto Weatherford-internal flat base
 *   - Card-vs-Cash toggle drives the hidden CC processing fee (2 % of subtotal)
 *
 * Manual fee inputs were removed in batch BE-2 — drivers have ZERO manual
 * price entry. Per spec, "Custom Trip Builder" is now "Rider · TAXIquote".
 */
export default function CustomTripPage() {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  // v2.6.9 batch BL · stops are stored as `{id, value}` objects so each row
  // has a stable React `key` even when stops are reordered or removed
  // (previously used array index → caused subtle state bugs on remove).
  const [stops, setStops] = useState([]);          // array of {id, value}
  const stopIdRef = useRef(0);
  const [pax, setPax] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customer, setCustomer] = useState({});
  const [closeoutOpen, setCloseoutOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const addStop = () => setStops((s) => [...s, { id: ++stopIdRef.current, value: "" }]);
  const setStopAt = (id, v) => setStops((s) => s.map((row) => (row.id === id ? { ...row, value: v } : row)));
  const removeStopAt = (id) => setStops((s) => s.filter((row) => row.id !== id));

  const calculate = async () => {
    if (!origin.trim() || !destination.trim()) {
      return toast.error("Enter both pickup and drop-off addresses");
    }
    setBusy(true);
    try {
      // v2.6.9 batch BE-4b · only forward stops that LOOK like real addresses
      // (must contain at least a digit OR a comma — pure single words like "asap"
      // are dropped silently so a careless stop entry doesn't blow up the whole quote).
      const cleanStops = stops
        .map((row) => (row.value || "").trim())
        .filter((s) => s && (/\d/.test(s) || s.includes(",")));
      const droppedStops = stops
        .map((row) => (row.value || "").trim())
        .filter((s) => s && !cleanStops.includes(s));
      if (droppedStops.length) {
        toast.warning(`Skipped ${droppedStops.length} stop${droppedStops.length>1?"s":""} that didn't look like an address (e.g. "${droppedStops[0]}"). Add a street address or remove it.`);
      }
      const { data } = await api.post("/driver/taxiquote", {
        origin: origin.trim(),
        destination: destination.trim(),
        passengers: pax,
        stops: cleanStops,
        ride_type: "single",
        payment_method: paymentMethod,
      });
      setResult(data);
      if (data.breakdown?.geofence_applied) toast.success("Weatherford in-city pricing applied ($8 flat)");
      else if (data.breakdown?.airport_fee_auto) toast.success(`Airport fee auto-applied (+$${data.breakdown.airport_fee})`);
      else if (data.breakdown?.toll_fee_auto) toast.success(`Toll-route detected — toll fee added (+$${data.breakdown.toll_fee})`);
      else toast.success(`Quote ready · $${data.subtotal}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Friendlier surface of Google's NOT_FOUND
      if (typeof detail === "string" && detail.includes("NOT_FOUND")) {
        toast.error("One of your addresses (or a stop) couldn't be found on Google Maps. Double-check that each address is a real street address.");
      } else {
        // v2.6.9 batch BM-3 · surface HTTP status + raw error so prod debug is possible
        toast.error(formatApiError(e, "Could not fetch quote"), { duration: 8000 });
      }
    } finally {
      setBusy(false);
    }
  };

  // Hand the locked-in quote off to the Statewide Live Meter (TAXIquote is
  // always a multi-jurisdiction priced trip — the Weatherford in-city meter
  // is reserved for flat-rate $5 inside-city-limits trips and doesn't fit).
  // The meter pre-fills addresses + miles + minutes + passengers; the GPS
  // still tracks actual distance so the driver can finalize on real data
  // while seeing the quoted target.
  const startTrip = () => {
    if (!result) return;
    const params = new URLSearchParams({
      origin, destination,
      miles: String(result.miles || 0),
      minutes: String(result.minutes || 0),
      passengers: String(pax),
      quote_total: String(result.subtotal || 0),
      payment_method: paymentMethod,
      // serialize merged customer info so the meter customer block pre-fills
      cust: JSON.stringify(customer || {}),
    });
    navigate(`/meter-statewide?${params.toString()}`);
  };

  const finalizeTrip = async (closeout) => {
    if (!result) return;
    setSaving(true);
    try {
      const merged = { ...customer };
      const customer_name = `${merged.first_name || ""} ${merged.last_name || ""}`.trim() || null;
      await api.post("/trips", {
        mode: "custom", origin, destination,
        miles: result.miles, minutes: result.minutes, passengers: pax, stops: stops.length,
        breakdown: result.breakdown, subtotal: result.subtotal, cc_surcharge: 0, total: result.subtotal,
        payment_method: closeout.payment_method,
        tip_amount: closeout.tip_amount,
        cash_tendered: closeout.cash_tendered,
        change_due: closeout.change_due,
        rating: closeout.rating,
        rating_comment: closeout.rating_comment,
        customer_name,
        customer_phone: merged.phone || null,
        customer_email: merged.email || null,
        customer_address: merged.address || null,
      });
      toast.success(`Trip closed out · ${closeout.rating}★`);
      setResult(null); setOrigin(""); setDestination(""); setStops([]); setCustomer({}); setCloseoutOpen(false);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Layout title="TAXIquote">
      <CustomerInfoForm value={customer} onChange={setCustomer} testidPrefix="custom-cust" className="mb-4" />

      <Card className="bg-zinc-900 border-zinc-800 p-6 mb-4">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-lg bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-400"><Calculator className="h-5 w-5"/></div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold">Rider · TAXIquote</h2>
            <p className="text-xs text-zinc-400">Enter pickup &amp; drop-off — the system pulls all rates &amp; fees automatically</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Pickup</Label>
            <AddressAutocomplete value={origin} onChange={setOrigin} placeholder="Pickup address" testid="custom-origin-input"/>
          </div>
          <div>
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Drop-off</Label>
            <AddressAutocomplete value={destination} onChange={setDestination} placeholder="Drop-off address" testid="custom-destination-input"/>
          </div>

          {/* Multi-stop ladder — $2.00 per extra stop is applied server-side */}
          {stops.map((row, i) => (
            <div key={row.id} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-[10px] text-zinc-400 uppercase tracking-wider">Stop {i + 1}</Label>
                <AddressAutocomplete value={row.value} onChange={(v) => setStopAt(row.id, v)} placeholder="Intermediate stop" testid={`custom-stop-${i}`}/>
              </div>
              <Button variant="ghost" size="sm" data-testid={`custom-stop-remove-${i}`} onClick={() => removeStopAt(row.id)} className="text-red-400 hover:text-red-300 mb-1">
                <X className="h-4 w-4"/>
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" data-testid="custom-add-stop" onClick={addStop} className="bg-zinc-950 border-zinc-700 text-amber-300 hover:bg-zinc-800">
            <Plus className="h-3.5 w-3.5 mr-1"/>Add Stop (+$2.00)
          </Button>

          {/* v2.6.9 batch BG · Route polyline preview — same TripMap component
              used on Live Meter + rider Book a Ride. Renders a Google Embed
              Directions iframe with the polyline once both addresses are filled. */}
          <TripMap
            origin={origin}
            destination={destination}
            stops={stops.map((row) => row.value).filter((s) => s && s.trim())}
            height={240}
            testid="taxiquote-map"
          />

          <div className="grid grid-cols-2 gap-3 pt-2">
            <PassengerPicker value={pax} onChange={setPax} label="Passengers" testid="custom-pax" min={1} max={6}/>
            <div>
              <Label className="text-[10px] text-zinc-400 uppercase tracking-wider">Payment Method</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                <button
                  type="button"
                  data-testid="custom-pay-cash"
                  onClick={() => setPaymentMethod("cash")}
                  className={`h-11 rounded-md border-2 font-bold uppercase text-xs tracking-widest transition-all ${
                    paymentMethod === "cash" ? "bg-green-500/15 border-green-400 text-green-400" : "bg-zinc-950 border-zinc-700 text-zinc-400"
                  }`}
                >Cash</button>
                <button
                  type="button"
                  data-testid="custom-pay-card"
                  onClick={() => setPaymentMethod("card")}
                  className={`h-11 rounded-md border-2 font-bold uppercase text-xs tracking-widest transition-all ${
                    paymentMethod === "card" ? "bg-amber-400/15 border-amber-400 text-amber-400" : "bg-zinc-950 border-zinc-700 text-zinc-400"
                  }`}
                >Card</button>
              </div>
            </div>
          </div>
        </div>

        <Button
          data-testid="custom-calc-btn"
          onClick={calculate}
          disabled={busy}
          className="w-full mt-5 h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "GENERATE QUOTE"}
        </Button>

        {/* Auto-applied fee callouts so the driver can SEE what the engine did */}
        {result?.breakdown && (
          <div className="mt-4 space-y-1 text-[11px]">
            {result.breakdown.geofence_applied && (
              <div className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-400/30 text-emerald-300" data-testid="auto-geofence">
                ✓ Weatherford in-city pricing · ${result.breakdown.geofence_flat_base?.toFixed(2)} flat base
              </div>
            )}
            {result.breakdown.airport_fee_auto && (
              <div className="px-2 py-1 rounded bg-amber-500/10 border border-amber-400/30 text-amber-300" data-testid="auto-airport">
                ✈️ OK airport detected · +${result.breakdown.airport_fee?.toFixed(2)} airport fee
              </div>
            )}
            {result.breakdown.toll_fee_auto && (
              <div className="px-2 py-1 rounded bg-amber-500/10 border border-amber-400/30 text-amber-300" data-testid="auto-toll">
                🛣️ Toll-route detected · +${result.breakdown.toll_fee?.toFixed(2)} toll fee
              </div>
            )}
            {paymentMethod === "card" && result.breakdown.cc_processing_fee > 0 && (
              <div className="px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-zinc-300" data-testid="auto-cc">
                💳 Credit Card Processing fee (driver waybill only) · +${result.breakdown.cc_processing_fee?.toFixed(2)} <span className="text-zinc-500">· hidden from rider view</span>
              </div>
            )}
          </div>
        )}
      </Card>

      {result && (
        <>
          <FareSummary result={result} onSave={() => setCloseoutOpen(true)} stripeAmountCents={Math.round(result.subtotal * 100)} hideCollectCard/>
          {/* v2.6.9 batch BE-2 · Start Trip handoff to the GPS-only Live Meter. */}
          <Button
            data-testid="custom-start-trip-btn"
            onClick={startTrip}
            className="w-full mt-3 h-12 bg-green-500 hover:bg-green-400 text-zinc-950 font-bold"
          >
            <MapPin className="h-4 w-4 mr-2"/>START TRIP &amp; OPEN LIVE METER
          </Button>
          <div className="mt-2">
            <TripNavButtons pickup={origin} dropoff={destination} testidPrefix="custom-nav"/>
          </div>
        </>
      )}

      <TripCloseoutDialog
        open={closeoutOpen}
        onOpenChange={setCloseoutOpen}
        subtotal={result?.subtotal || 0}
        cc_surcharge={result?.breakdown?.cc_processing_fee || 0}
        total={result?.subtotal || 0}
        defaultPaymentMethod={paymentMethod}
        cardCollected={false}
        onConfirm={finalizeTrip}
        busy={saving}
      />
    </Layout>
  );
}
